// src/components/Projects/CreateProjectDialog.tsx
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useProjects } from '@/hooks/useProjects';

const PROJECT_COLORS = [
  { value: '#6366F1', label: 'Índigo' },
  { value: '#10B981', label: 'Verde' },
  { value: '#F59E0B', label: 'Âmbar' },
  { value: '#EF4444', label: 'Vermelho' },
  { value: '#8B5CF6', label: 'Violeta' },
  { value: '#EC4899', label: 'Rosa' },
];

interface CreateProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function CreateProjectDialog({
  open,
  onOpenChange,
}: CreateProjectDialogProps) {
  const { createProject, isCreating } = useProjects();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedColor, setSelectedColor] = useState(PROJECT_COLORS[0].value);

  const handleClose = () => {
    setName('');
    setDescription('');
    setSelectedColor(PROJECT_COLORS[0].value);
    onOpenChange(false);
  };

  const handleSubmit = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) return;

    try {
      await createProject({
        name: trimmedName,
        description: description.trim() || undefined,
        color: selectedColor,
      });
      handleClose();
    } catch {
      // erro tratado no hook (toast)
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Novo projeto</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          {/* Nome */}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="project-name">
              Nome <span className="text-destructive">*</span>
            </Label>
            <Input
              id="project-name"
              placeholder="Ex: Campanha de vendas Q3"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) handleSubmit();
              }}
              autoFocus
            />
          </div>

          {/* Descrição */}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="project-description">Descrição</Label>
            <Textarea
              id="project-description"
              placeholder="Descreva o objetivo deste projeto..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="resize-none"
            />
          </div>

          {/* Cor */}
          <div className="flex flex-col gap-2">
            <Label>Cor do projeto</Label>
            <div className="flex items-center gap-2">
              {PROJECT_COLORS.map((color) => (
                <div
                  key={color.value}
                  className="h-7 w-7 rounded-full cursor-pointer transition-transform hover:scale-110 flex items-center justify-center"
                  style={{ backgroundColor: color.value }}
                  title={color.label}
                  onClick={() => setSelectedColor(color.value)}
                >
                  {selectedColor === color.value && (
                    <div className="h-2.5 w-2.5 rounded-full bg-white shadow" />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Preview */}
          <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border border-border">
            <div
              className="h-8 w-1 rounded-full flex-shrink-0"
              style={{ backgroundColor: selectedColor }}
            />
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">
                {name.trim() || 'Nome do projeto'}
              </p>
              {description.trim() && (
                <p className="text-xs text-muted-foreground truncate">
                  {description.trim()}
                </p>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isCreating}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!name.trim() || isCreating}
          >
            {isCreating ? 'Criando...' : 'Criar projeto'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
